import { Box, Checkbox, Typography } from '@mui/material';
import { BuilderAnalysisType } from '../../types/SimilarlySituated/BuilderAnalysisType.type';
import { SsbOutcome } from '../../types/SimilarlySituated/SsbOutcome.type';
import { Statute } from '../../types/Statute.type';
import { analysisStatuteKind, isStatuteAnalysisType } from '../../utils/ssbBuilderBox';
import { BUTTON_RESET } from '../../utils/ssbStyles';
import RemovableChip from './RemovableChip';
import StatuteSelectorTray from './StatuteSelectorTray';

interface OutcomeRowProps {
  outcomeType: BuilderAnalysisType;
  outcome?: SsbOutcome;
  countyId?: number | null;
  isEditing: boolean;
  onToggle: () => void;
  onAdd: () => void;
  onCancelEditing: () => void;
  onStatuteSelected: (statutes: Statute[]) => void;
  onStatutesChange: (statutes: Statute[]) => void;
}

const LINK_BUTTON = {
  ...BUTTON_RESET,
  color: '#718096',
  fontSize: '12px',
  fontWeight: 500,
  transition: 'color 0.2s ease-in-out',
  ':hover': { color: 'primary.main' },
  padding: '2px 4px',
};

function OutcomeRow({
  outcomeType,
  outcome,
  countyId,
  isEditing,
  onToggle,
  onAdd,
  onCancelEditing,
  onStatuteSelected,
  onStatutesChange,
}: OutcomeRowProps) {
  const isStatuteInput = isStatuteAnalysisType(outcomeType);
  const isSelected = Boolean(outcome);
  const statutes = outcome?.statutes ?? [];
  // a statute outcome only counts once it holds a statute
  const hasSelection = isSelected && (!isStatuteInput || statutes.length > 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: isSelected ? '#132B50' : '#CBD5E0',
          borderRadius: '8px',
          backgroundColor: hasSelection ? '#EFF6FF' : '#fff',
          padding: '10px 16px 10px 8px',
          boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Checkbox size='small' sx={{ color: '#CBD5E0' }} checked={isSelected} onChange={onToggle} />
          <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{outcomeType.name}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          {isStatuteInput ? (
            <Box
              component='button'
              type='button'
              onClick={isEditing ? onCancelEditing : onAdd}
              sx={{
                border: '1px solid #1A365D',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                padding: '6px 12px',
                fontSize: '12px',
                fontWeight: 600,
                color: '#1A365D',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': { backgroundColor: 'primary.main', color: '#fff' },
              }}
            >
              {isEditing ? 'cancel' : '+ add'}
            </Box>
          ) : (
            <Box component='button' type='button' onClick={onToggle} sx={LINK_BUTTON}>
              {isSelected ? 'remove' : 'select'}
            </Box>
          )}
        </Box>

        {hasSelection && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '4px 0 4px 42px' }}>
            {isStatuteInput ? (
              statutes.map((statute) => (
                <RemovableChip
                  key={statute.id}
                  variant='filled'
                  label={statute.display_name}
                  onRemove={() => onStatutesChange(statutes.filter((s) => s.id !== statute.id))}
                />
              ))
            ) : (
              <RemovableChip variant='filled' label={outcomeType.name} onRemove={onToggle} />
            )}
          </Box>
        )}
      </Box>

      {isEditing && (
        <Box sx={{ border: '2px solid #DBEAFE', borderRadius: '8px', padding: '12px' }}>
          <StatuteSelectorTray
            statutes={statutes}
            countyId={countyId}
            kind={analysisStatuteKind(outcomeType)}
            showSelected={false}
            initiallyOpen
            closeOnSelect
            onChange={onStatuteSelected}
          />
        </Box>
      )}
    </Box>
  );
}

export default OutcomeRow;
